import { useEffect } from 'react'
import { useRecoilCallback, useRecoilValue, useSetRecoilState } from 'recoil'
import { editorPagesAtom, layoutUsedFontsAtom } from '@/atoms/_editorAtoms'
import {
  canonicalFontName,
  extractFontFamiliesFromHtml,
} from '@/utils/editor-text-box/editorTextBoxAdapter'
import { sameStringList } from '@/utils/editor/sameStringList'
import {
  TINYMCE_FONT_CHANGE_EVENT,
  type FontChangeEventDetail,
} from '@/utils/tinyMCE/fontChangeEvent'
import type { Content, Item, TextItem } from '@messenger/types'

export { extractFontFamiliesFromHtml }

const isTextItem = (item: Item): item is TextItem => item.type === 'text'

export const extractUsedFontsFromPages = (pages: Content[]): string[] => {
  const usedFonts = new Set<string>()

  for (const page of pages) {
    const items = page.product?.items ?? []
    for (const item of items) {
      if (isTextItem(item) && item.text?.value) {
        const fonts = extractFontFamiliesFromHtml(item.text.value)
        for (const font of fonts) {
          usedFonts.add(font)
        }
      }
    }
  }

  return Array.from(usedFonts).sort((a, b) => a.localeCompare(b))
}

/**
 * Tracks the set of fonts applied anywhere in the current layout's text items
 * and keeps `layoutUsedFontsAtom` in sync. The list seeds from the loaded
 * pages and grows live as the designer applies fonts: the FontName command
 * post-hook in commandHandler.js dispatches a `tinymce:fontchange` window
 * event, which we fold in without re-scanning every page. The customFontSelect
 * plugin reads the resulting list to render a "Used in this layout" section at
 * the top of the font dropdown (#2435).
 */
export const useLayoutUsedFonts = () => {
  const editorPages = useRecoilValue(editorPagesAtom)
  const setLayoutUsedFonts = useSetRecoilState(layoutUsedFontsAtom)

  const refreshLayoutFonts = useRecoilCallback(
    ({ snapshot, set }) =>
      () => {
        const usedFonts = extractUsedFontsFromPages(editorPages)
        const current = snapshot.getLoadable(layoutUsedFontsAtom).valueMaybe()
        if (sameStringList(current, usedFonts)) return
        set(layoutUsedFontsAtom, usedFonts)
      },
    [editorPages],
  )

  useEffect(() => {
    refreshLayoutFonts()
  }, [refreshLayoutFonts])

  useEffect(() => {
    const handleFontChange = (e: CustomEvent<FontChangeEventDetail>) => {
      const fontName = e.detail?.fontName
      if (!fontName) return

      const matched = canonicalFontName(fontName)
      if (!matched) return

      setLayoutUsedFonts((prev) => {
        if (prev.includes(matched)) return prev
        return [...prev, matched].sort((a, b) => a.localeCompare(b))
      })
    }

    window.addEventListener(
      TINYMCE_FONT_CHANGE_EVENT,
      handleFontChange as EventListener,
    )
    return () => {
      window.removeEventListener(
        TINYMCE_FONT_CHANGE_EVENT,
        handleFontChange as EventListener,
      )
    }
  }, [setLayoutUsedFonts])

  return { refreshLayoutFonts }
}

export default useLayoutUsedFonts
